import { getField, updateField } from "vuex-map-fields";
import { VuexModule, Module, Action } from "vuex-module-decorators";
import ShiftService from "@/services/shift.service";
import RatingService from "@/services/rating.service";
import OfficeService from "@/services/office-profile.service";
import { 
  AcceptedShift,
  OpenShift,
  RequestedShift,
  RequestedWorker,
  Shift
} from "./shift.types";
import { OfficeDto } from "@/types/office.type";
import { PositionDto } from "@/types/position.type";
import { PayRateStatisticsDto, ShiftDataDto, ShiftDeleteFormDataDto, ShiftDeleteFormDto, ShiftFilterDto, ShiftGroupDto, ShiftGroupFormDataDto, ShiftGroupFormDto } from "@/types/shift.type";
import { ShiftGroupResponseDto, ShiftGroupResponseStatusFormDto } from "@/types/shift-response.type";
import shiftService from "@/services/shift.service";
import { RatingStatus, ResponseStatus, ShiftStatus } from "@/common/constants";
import { RatingDto } from "@/types/rating.type";

@Module({ namespaced: true })
class ShiftModule extends VuexModule {
  openShifts: OpenShift[] = [];
  requestedShifts: RequestedShift[] = [];
  confirmedShifts: AcceptedShift[] = [];
  removedShifts: Shift[] = [];
  expiredShifts: Shift[] = [];
  positions: PositionDto[] = [];
  openRatings: RatingDto[] = [];
  offices: OfficeDto[] = [];
  selectedOffice: OfficeDto | null = null;
  payRateStatistics: PayRateStatisticsDto | null = null;
  shiftFormData: ShiftGroupFormDataDto | null = null;
  deleteFormData: ShiftDeleteFormDataDto | null = null;
  shiftData: ShiftDataDto | null = null;
  shiftForm = new ShiftGroupFormDto();
  deleteForm = new ShiftDeleteFormDto();
  shiftFilters = new ShiftFilterDto();

  loading = false;

  @Action({ rawError: true })
  async deleteShift(): Promise<void> {
    await ShiftService.deleteAsync(this.deleteForm);
  }

  @Action({ rawError: true })
  async getDeleteFormData(): Promise<void> {
    const formData = await ShiftService.getDeleteFormDataAsync();

    this.context.commit("updateField", {
      path: "deleteFormData",
      value: formData
    });
  }

  @Action({ rawError: true })
  async getOfficeRatings(officeId: number): Promise<void> {
    if (!this.shiftData) {
      return;
    }

    const ratings = await RatingService.getOpenByOfficeAsync(officeId);
    const ratingOpenStatus = RatingStatus.Open.getId(this.shiftData.ratingStatuses);
    
    const openRatings = ratings
      .filter(rating => rating.status === ratingOpenStatus);
      
    this.context.commit("updateField", {
      path: "openRatings",
      value: openRatings
    });
  }

  @Action({ rawError: true })
  async getOffices({ userId, officeId }: { userId: number, officeId: number | undefined, }): Promise<void> {
    try {
      const offices = await ShiftService.getOfficesAsync(userId);

      this.context.commit("updateField", {
        path: "offices",
        value: offices
      });

      const office = officeId ? offices.find(office => office.id === officeId) : offices[0];

      this.context.commit("updateField", {
        path: "selectedOffice",
        value: office
      });
    } catch (e) {
      console.error(e);
    }
  }

  @Action({ rawError: true })
  async saveShift(form: ShiftGroupFormDto): Promise<void> {
    try {
      await ShiftService.saveAsync(form);
    } catch (e) {
      console.error(e);
    }
  }

  @Action({ rawError: true })
  async getShiftForm(shiftGroupId: number): Promise<void> {
    try {
      const form = await ShiftService.getFormAsync(shiftGroupId);

      if (!form.positionId) {
        return;
      }

      const statistics = await ShiftService.getPayRateStatisticsAsync(form.positionId);
      const selectedOffice = this.offices.find(office => office.id === form.officeId);
      
      this.context.commit("updateField", {
        path: "shiftForm",
        value: form
      });
      this.context.commit("updateField", {
        path: "payRateStatistics",
        value: statistics
      });
      this.context.commit("updateField", {
        path: "selectedOffice",
        value: selectedOffice
      })
    } catch (e) {
      console.log(e);
    }
  }

  @Action({ rawError: true })
  async getShiftFormData(officeId: number): Promise<void> {
    try {
      const data = await ShiftService.getFormDataAsync(officeId);

      this.context.commit("updateField", {
        path: "shiftFormData",
        value: data
      });
    } catch (e) {
      console.log(e);
    }
  }

  @Action({ rawError: true })
  async getPayRateStatistics(positionId: number): Promise<void> {
    const statistics = await ShiftService.getPayRateStatisticsAsync(positionId);

    this.context.commit("updateField", {
      path: "payRateStatistics",
      value: statistics
    });
  }

  @Action({ rawError: true })
  async updateShiftResponseStatus(form: ShiftGroupResponseStatusFormDto): Promise<void> {
    try {
      await shiftService.updateResponseStatusAsync(form);
    } catch (e) {
      console.error(e);
    }
  }

  @Action({ rawError: true })
  async getShiftData(): Promise<void> {
    const shiftData = await ShiftService.getDataAsync();

    this.context.commit("updateField", {
      path: "shiftData",
      value: shiftData
    });
  }

  @Action({ rawError: true })
  async getShifts(filter?: ShiftFilterDto): Promise<void> {
    try {
      if (!filter) {
        filter = new ShiftFilterDto();
      }
      
      if (!this.shiftData) {
        return;
      }

      const shiftConfirmedStatus = ShiftStatus.Confirmed.getId(this.shiftData.shiftStatuses);
      const shiftOpenStatus = ShiftStatus.Open.getId(this.shiftData.shiftStatuses);
      const shiftPendingStatus = ShiftStatus.Pending.getId(this.shiftData.shiftStatuses);
      const shiftRequestedStatus = ShiftStatus.Requested.getId(this.shiftData.shiftStatuses);
      const shiftRemovedStatus = ShiftStatus.Removed.getId(this.shiftData.shiftStatuses);
      const shiftExpiredStatus = ShiftStatus.Expired.getId(this.shiftData.shiftStatuses);
      const shiftExpiredV2Status = ShiftStatus.ExpiredV2.getId(this.shiftData.shiftStatuses);
      const responseAcceptedStatus = ResponseStatus.Accepted.getId(this.shiftData.responseStatuses);
      const responseCancelledStatus = ResponseStatus.Cancelled.getId(this.shiftData.responseStatuses);
      const responseConfirmedStatus = ResponseStatus.Confirmed.getId(this.shiftData.responseStatuses);
      const responseLateStatus = ResponseStatus.RunningLate.getId(this.shiftData.responseStatuses);

      const shifts = await ShiftService.getOfficeShiftsAsync(filter);

      const hasActiveResponses = (shift: ShiftGroupDto): boolean =>
        shift.responses.some((response: ShiftGroupResponseDto) => response.status !== responseCancelledStatus);

      const hasAcceptedLikeResponse = (shift: ShiftGroupDto): boolean =>
        shift.responses.some((response: ShiftGroupResponseDto) =>
          response.status === responseAcceptedStatus ||
          response.status === responseConfirmedStatus ||
          response.status === responseLateStatus
        );

      const openShifts: OpenShift[] = shifts
        .filter(openShift =>
          openShift.status === shiftOpenStatus ||
          (openShift.status === shiftPendingStatus && !hasActiveResponses(openShift))
        )
        .map(openShift => {
          return {
            ...new Shift(openShift, ShiftStatus.Open),
            favoriteOnly: openShift.favoriteOnly
          }
        });
      const requestedShifts: RequestedShift[] = shifts
        .filter(requestedShift =>
          requestedShift.status === shiftRequestedStatus &&
          hasActiveResponses(requestedShift)
        )
        .map(requestedShift => {
          const requestedWorkers: RequestedWorker[] = requestedShift.responses
            .map((response: ShiftGroupResponseDto) => new RequestedWorker(response));
        
          return {
            ...new Shift(requestedShift, ShiftStatus.Requested),
            requestedWorkers
          };
        });
      const confirmedShifts: AcceptedShift[] = (responseAcceptedStatus && responseConfirmedStatus && responseLateStatus)
        ? shifts
          .filter(confirmedShift =>
            (confirmedShift.status === shiftConfirmedStatus || confirmedShift.status === shiftPendingStatus) &&
            hasAcceptedLikeResponse(confirmedShift)
          )
          .map(confirmedShift => new AcceptedShift(
            confirmedShift,
            [responseConfirmedStatus, responseLateStatus, responseAcceptedStatus],
            confirmedShift.status === shiftPendingStatus ? ShiftStatus.Pending : ShiftStatus.Confirmed
          ))
        : [];
      const removedShifts: Shift[] = shifts
        .filter(removedShift => removedShift.status === shiftRemovedStatus)
        .map(removedShift => new Shift(removedShift, ShiftStatus.Removed));
      const expiredShifts: Shift[] = shifts
        .filter(expiredShift => expiredShift.status === shiftExpiredStatus || expiredShift.status === shiftExpiredV2Status)
        .map(expiredShift => new Shift(expiredShift, ShiftStatus.Expired))

      this.context.commit("updateField", {
        path: "openShifts",
        value: openShifts
      });
      this.context.commit("updateField", {
        path: "requestedShifts",
        value: requestedShifts
      });
      this.context.commit("updateField", {
        path: "confirmedShifts",
        value: confirmedShifts
      });
      this.context.commit("updateField", {
        path: "removedShifts",
        value: removedShifts
      });
      this.context.commit("updateField", {
        path: "expiredShifts",
        value: expiredShifts
      });
    } catch (e) {
      console.error(e);
    }
  }

  @Action({ rawError: true })
  async createOfficeFavorite(userId: number): Promise<void> {
    try {
      if (!this.selectedOffice) {
        return;
      }

      await OfficeService.createFavoriteAsync(this.selectedOffice.id, userId);
    } catch (e) {
      console.error(e);
    }
  }

  @Action({ rawError: true })
  async deleteOfficeFavorite(userId: number): Promise<void> {
    try {
      if (!this.selectedOffice) {
        return;
      }

      await OfficeService.deleteFavoriteAsync(this.selectedOffice.id, userId);
    } catch (e) {
      console.error(e);
    }
  }

  @Action({ rawError: true })
  resetJobPosts(): void {
    this.context.commit("updateField", {
      path: "openJobs",
      value: []
    });
    this.context.commit("updateField", {
      path: "requestedJobs",
      value: []
    });
    this.context.commit("updateField", {
      path: "confirmedJobs",
      value: []
    });
  }
}

ShiftModule.getters = { getField };
ShiftModule.mutations = { updateField };

export default ShiftModule;